import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CreditCard, Plus, Search, Loader2, Calendar, Filter,
  Edit, Trash2, Eye, FileText
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

export default function Expenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expenseItems, setExpenseItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    expense_item_id: '',
    supplier_id: '',
    account_id: '',
    amount: '',
    currency: 'TRY',
    expense_date: new Date().toISOString().split('T')[0],
    description: '',
    invoice_no: ''
  });

  useEffect(() => {
    fetchExpenses();
    fetchExpenseItems();
    fetchSuppliers();
    fetchAccounts();
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/expenses');
      setExpenses(res.data || []);
    } catch (err) {
      // Endpoint henüz yoksa boş liste göster
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseItems = async () => {
    try {
      const res = await api.get('/expense-items');
      setExpenseItems(res.data || []);
    } catch (err) {
      setExpenseItems([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data || []);
    } catch (err) {
      setSuppliers([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/treasury/accounts');
      setAccounts(res.data || []);
    } catch (err) {
      setAccounts([]);
    }
  };

  const openNewDialog = () => {
    setEditingExpense(null);
    setForm({
      expense_item_id: '',
      supplier_id: '',
      account_id: '',
      amount: '',
      currency: 'TRY',
      expense_date: new Date().toISOString().split('T')[0],
      description: '',
      invoice_no: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setForm({
      expense_item_id: expense.expense_item_id || '',
      supplier_id: expense.supplier_id || '',
      account_id: expense.account_id || '',
      amount: expense.amount || '',
      currency: expense.currency || 'TRY',
      expense_date: expense.expense_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      description: expense.description || '',
      invoice_no: expense.invoice_no || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.expense_item_id || !form.amount) {
      toast.error('Gider kalemi ve tutar zorunludur');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount)
      };

      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, payload);
        toast.success('Masraf güncellendi');
      } else {
        await api.post('/expenses', payload);
        toast.success('Masraf kaydedildi');
      }
      
      setDialogOpen(false);
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu masrafı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Masraf silindi');
      fetchExpenses();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const getExpenseItemName = (id) => {
    const item = expenseItems.find(i => i.id === id);
    return item?.name || '-';
  };

  const getSupplierName = (id) => {
    const supplier = suppliers.find(s => s.id === id);
    return supplier?.name || '-';
  };

  const getAccountName = (id) => {
    const account = accounts.find(a => a.id === id);
    return account?.name || '-';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916]">Masraflar</h1>
          <p className="text-sm text-[#8a7e70]">İşletme gider ve masraf kayıtları</p>
        </div>
        <Button onClick={openNewDialog} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
          <Plus size={16} className="mr-2" /> Yeni Masraf
        </Button>
      </div>

      {/* Expenses Table */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
            <CreditCard size={18} /> Masraf Listesi
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={24} />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12 text-[#8a7e70]">
              <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
              <p>Henüz masraf kaydı yok</p>
              <Button onClick={openNewDialog} variant="outline" className="mt-4">
                <Plus size={16} className="mr-2" /> İlk Masrafı Ekle
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Gider Kalemi</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Tedarikçi/Kişi</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Açıklama</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Fatura No</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">Tutar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold">Ödeme Hesabı</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} className="hover:bg-[#f5f0e8]">
                    <TableCell>{new Date(expense.expense_date).toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell className="font-medium">{getExpenseItemName(expense.expense_item_id)}</TableCell>
                    <TableCell>{getSupplierName(expense.supplier_id)}</TableCell>
                    <TableCell className="text-sm text-[#8a7e70]">{expense.description || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{expense.invoice_no || '-'}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {expense.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {expense.currency}
                    </TableCell>
                    <TableCell>{getAccountName(expense.account_id)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(expense)}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#faf7f2]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916]">
              {editingExpense ? 'Masraf Düzenle' : 'Yeni Masraf'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Expense Item */}
            <div>
              <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                Gider Kalemi *
              </label>
              <Select value={form.expense_item_id} onValueChange={(v) => setForm({...form, expense_item_id: v})}>
                <SelectTrigger className="bg-white border-[#d0c4b0]">
                  <SelectValue placeholder="Gider kalemi seçin" />
                </SelectTrigger>
                <SelectContent>
                  {expenseItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                Tarih
              </label>
              <Input
                type="date"
                value={form.expense_date}
                onChange={(e) => setForm({...form, expense_date: e.target.value})}
                className="bg-white border-[#d0c4b0]"
              />
            </div>

            {/* Amount & Currency */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                  Tutar *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({...form, amount: e.target.value})}
                  placeholder="0.00"
                  className="bg-white border-[#d0c4b0]"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                  Para Birimi
                </label>
                <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Supplier */}
            <div>
              <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                Tedarikçi / Kişi
              </label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({...form, supplier_id: v})}>
                <SelectTrigger className="bg-white border-[#d0c4b0]">
                  <SelectValue placeholder="Opsiyonel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Seçilmedi</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Account */}
            <div>
              <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                Ödeme Hesabı
              </label>
              <Select value={form.account_id} onValueChange={(v) => setForm({...form, account_id: v})}>
                <SelectTrigger className="bg-white border-[#d0c4b0]">
                  <SelectValue placeholder="Ödeme yapılan hesap" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Seçilmedi</SelectItem>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.currency})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Invoice No */}
            <div>
              <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                Fatura / Fiş No
              </label>
              <Input
                value={form.invoice_no}
                onChange={(e) => setForm({...form, invoice_no: e.target.value})}
                placeholder="Opsiyonel"
                className="bg-white border-[#d0c4b0]"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                Açıklama
              </label>
              <Input
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Opsiyonel"
                className="bg-white border-[#d0c4b0]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              {editingExpense ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
